import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { WidgetPanelControls, WidgetPanelStatus } from '../Dashboard/WidgetPanelSlot';
import useMarketIntelligenceStore, {
  getMarketIntelligencePostId,
  getPostTimestamp,
} from '../../Store/marketIntelligenceStore';
import usePlaybackStore from '../../Store/playbackStore';
import {
  disableMarketIntelligenceAlarm,
  enableMarketIntelligenceAlarm,
  isMarketIntelligenceAlarmEnabled,
} from '../../Utils/alarmService';
import { useMarketOpen } from '../../Hooks/useMarketOpen';
import { etDateKey, isMarketSessionDate, mostRecentSessionDate } from '../../Utils/marketHours';
import '../../Styles/MarketIntelligenceFeedWidget.css';

const MODULE_LABELS = {
  first_flows: 'FIRST FLOWS',
  zero_gex: '0 GEX',
  trend: 'TREND',
  eod_range: 'EOD RANGE',
  market: 'MARKET INTELLIGENCE',
  null: 'MARKET INTELLIGENCE',
};

/*
  Module-specific CSS classes.

  These are intentionally separate from the module labels so the
  visual styling can be changed entirely from CSS without touching
  the post normalization or display logic again.
*/
const MODULE_CLASSES = {
  first_flows: 'module-first-flows',
  zero_gex: 'module-zero-gex',
  trend: 'module-trend',
  eod_range: 'module-eod-range',
  market: 'module-market',
  null: 'module-market',
};

const PRODUCER_LABELS = {
  first_flows_posting_agent: 'First Flows Agent',
  zero_gex_posting_agent: '0 GEX Agent',
  trend_posting_agent: 'Trend Agent',
  eod_range_posting_agent: 'EOD Range Agent',
  market_intelligence_organizer: 'Market Intelligence Organizer',
};

const STATUS_LABELS = {
  active: 'ACTIVE',
  watching: 'WATCHING',
  mixed: 'MIXED',
  no_trade: 'NO TRADE',
  completed: 'COMPLETE',
};

const EXPLICIT_LIFECYCLE_FIELDS = [
  'lifecycle_state',
  'lifecycleState',
];

const EXPLICIT_ACTIVE_FIELDS = [
  'is_active',
  'isActive',
];

const EMPTY_POSTS = [];

function cleanLabel(value) {
  if (!value && value !== 0) return '-';

  return String(value)
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\bGex\b/g, 'GEX')
    .replace(/\bSpx\b/g, 'SPX')
    .replace(/\bEod\b/g, 'EOD')
    .replace(/\bAtm\b/g, 'ATM');
}

function getDisplayPayload(post) {
  return post?.display_payload || post?.displayPayload || {};
}

function getRawModule(post) {
  const displayPayload = getDisplayPayload(post);

  return (
    post?.source_module ||
    post?.sourceModule ||
    displayPayload.module ||
    displayPayload.module_label ||
    displayPayload.moduleLabel ||
    'market'
  );
}

function getModuleLabel(post) {
  const raw = getRawModule(post);

  return MODULE_LABELS[raw] || cleanLabel(raw).toUpperCase();
}

function getModuleClass(post) {
  const raw = getRawModule(post);

  return MODULE_CLASSES[raw] || 'module-market';
}

function getStatusLabel(post) {
  const displayPayload = getDisplayPayload(post);

  return (
    displayPayload.status_label ||
    displayPayload.statusLabel ||
    STATUS_LABELS[post?.status] ||
    cleanLabel(post?.status).toUpperCase()
  );
}

function getAgentLabel(post) {
  const producerName = post?.producer_name || post?.producerName;

  return PRODUCER_LABELS[producerName] || cleanLabel(producerName);
}

function getConfidence(post) {
  const displayPayload = getDisplayPayload(post);

  return (
    displayPayload.confidence_label ||
    displayPayload.confidenceLabel ||
    displayPayload.confidence ||
    null
  );
}

function getBias(post) {
  const displayPayload = getDisplayPayload(post);

  return (
    displayPayload.bias_label ||
    displayPayload.biasLabel ||
    displayPayload.bias ||
    null
  );
}

function getTimeET(value) {
  if (!value) return '-';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '-';

  return date.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function getPostTimestampValue(value) {
  const date = new Date(value);
  const timestamp = date.getTime();

  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function getPostTradingDate(post) {
  const explicitTradingDate =
    post?.trading_date ||
    post?.tradingDate ||
    null;

  if (explicitTradingDate) return explicitTradingDate;

  const timestamp = getPostTimestampValue(
    getPostTimestamp(post)
  );

  return timestamp ? etDateKey(timestamp) : null;
}

function normalizeKeyLevels(keyLevels) {
  if (
    !keyLevels ||
    typeof keyLevels !== 'object' ||
    Array.isArray(keyLevels)
  ) {
    return [];
  }

  return Object.entries(keyLevels)
    .filter(([, value]) => (
      value !== null &&
      value !== undefined &&
      value !== ''
    ))
    .flatMap(([key, value]) => {
      if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value)
      ) {
        return Object.entries(value)
          .filter(([, nestedValue]) => (
            nestedValue !== null &&
            nestedValue !== undefined &&
            String(nestedValue).trim()
          ))
          .map(([nestedKey, nestedValue]) => ({
            label: `${cleanLabel(key)} / ${cleanLabel(nestedKey)}`,
            value: Array.isArray(nestedValue)
              ? nestedValue.join(', ')
              : String(nestedValue),
          }));
      }

      return [{
        label: cleanLabel(key),
        value: Array.isArray(value)
          ? value.join(', ')
          : String(value),
      }];
    });
}

function normalizeCardSections(cardSections) {
  if (!cardSections) return [];

  if (Array.isArray(cardSections)) {
    return cardSections.map((section, index) => {
      const title =
        section.title ||
        section.label ||
        `Section ${index + 1}`;

      const raw =
        section.content ||
        section.text ||
        section.value ||
        section.items ||
        '';

      return {
        title: cleanLabel(title),
        items: Array.isArray(raw) ? raw : [raw],
      };
    });
  }

  if (typeof cardSections === 'object') {
    return Object.entries(cardSections).map(([key, value]) => ({
      title: cleanLabel(key),
      items: Array.isArray(value) ? value : [value],
    }));
  }

  return [];
}

function getPriorityClass(priority) {
  const number = Number(priority);

  if (number <= 1) return 'critical';
  if (number === 2) return 'high';
  if (number === 3) return 'medium';
  if (number >= 4) return 'low';

  return 'normal';
}

function getExplicitLifecycleState(
  post,
  displayPayload = post?.display_payload || {}
) {
  const explicitState = EXPLICIT_LIFECYCLE_FIELDS
    .map((field) => displayPayload?.[field])
    .find((value) => (
      value !== null &&
      value !== undefined &&
      String(value).trim()
    ));

  const normalizedExplicitState = explicitState
    ? String(explicitState).trim().toLowerCase()
    : null;

  if (normalizedExplicitState === 'expired') {
    return 'expired';
  }

  if (normalizedExplicitState === 'inactive') {
    return 'inactive';
  }

  if (normalizedExplicitState === 'active') {
    return 'active';
  }

  if (post?.status === 'expired') {
    return 'expired';
  }

  if (post?.status === 'inactive') {
    return 'inactive';
  }

  const explicitIsActive = EXPLICIT_ACTIVE_FIELDS
    .map((field) => (
      displayPayload?.[field] ??
      post?.[field]
    ))
    .find((value) => (
      value === true ||
      value === false
    ));

  if (explicitIsActive === false) {
    return 'inactive';
  }

  if (explicitIsActive === true) {
    return 'active';
  }

  return null;
}

function normalizePost(post) {
  const displayPayload = getDisplayPayload(post);
  const rawModule = getRawModule(post);
  const createdAt = getPostTimestamp(post) || null;

  const tradingDate = getPostTradingDate(post);

  return {
    id: getMarketIntelligencePostId(post),

    createdAt,
    tradingDate,

    moduleLabel: getModuleLabel(post),
    moduleClass: getModuleClass(post),

    statusLabel: getStatusLabel(post),
    agentLabel: getAgentLabel(post),

    confidence: getConfidence(post),
    bias: getBias(post),

    headline:
      post.headline ||
      'Market Intelligence Update',

    body:
      post.body ||
      '',

    summaryBullets: Array.isArray(post.summary_bullets || post.summaryBullets)
      ? post.summary_bullets || post.summaryBullets
      : [],

    keyLevels:
      normalizeKeyLevels(post.key_levels || post.keyLevels),

    cardSections:
      normalizeCardSections(
        displayPayload.card_sections || displayPayload.cardSections
      ),

    tags: Array.isArray(displayPayload.tags)
      ? displayPayload.tags
      : [],

    priorityClass:
      getPriorityClass(post.priority),

    sourceModule:
      rawModule,

    sortTimestamp:
      getPostTimestampValue(createdAt),

    explicitLifecycleState:
      getExplicitLifecycleState(
        post,
        displayPayload
      ),
  };
}

export function buildMarketIntelligenceFeedPosts(posts = []) {
  const normalizedPosts = (posts || [])
    .map(normalizePost)
    .sort((a, b) => (
      b.sortTimestamp - a.sortTimestamp
    ));

  const latestTradingDate = normalizedPosts.reduce(
    (latest, post) => {
      if (!post.tradingDate) {
        return latest;
      }

      if (
        !latest ||
        post.tradingDate > latest
      ) {
        return post.tradingDate;
      }

      return latest;
    },
    null
  );

  const seenModuleTradingDates = new Set();

  return normalizedPosts.map((post) => {
    let lifecycleState =
      post.explicitLifecycleState;

    const moduleTradingDateKey =
      post.sourceModule &&
      post.tradingDate
        ? `${post.sourceModule}|${post.tradingDate}`
        : null;

    const hasNewerSameDayPost =
      moduleTradingDateKey
        ? seenModuleTradingDates.has(
            moduleTradingDateKey
          )
        : false;

    if (
      !lifecycleState &&
      latestTradingDate &&
      post.tradingDate &&
      post.tradingDate < latestTradingDate
    ) {
      lifecycleState = 'expired';
    }

    if (
      !lifecycleState &&
      hasNewerSameDayPost
    ) {
      lifecycleState = 'inactive';
    }

    if (moduleTradingDateKey) {
      seenModuleTradingDates.add(
        moduleTradingDateKey
      );
    }

    return {
      ...post,

      lifecycleState,

      lifecycleLabel:
        lifecycleState &&
        lifecycleState !== 'active'
          ? lifecycleState.toUpperCase()
          : null,
    };
  });
}

export function selectMarketIntelligencePostsForTradingDate({
  recentPosts = [],
  visiblePosts = [],
  historicalPosts = [],
  tradingDate,
  cutoffMs = null,
}) {
  const forTradingDate = (posts) => posts.filter((post) => (
    getPostTradingDate(post) === tradingDate
  ));

  const recentForTradingDate = forTradingDate(recentPosts);
  const visibleForTradingDate = forTradingDate(visiblePosts);
  const historicalForTradingDate = forTradingDate(historicalPosts);

  const atCutoff = (posts) => {
    if (cutoffMs == null) return posts;

    return posts.filter((post) => {
      const timestamp = getPostTimestampValue(
        getPostTimestamp(post)
      );

      return timestamp === 0 || timestamp <= cutoffMs;
    });
  };

  const recentAtCutoff = atCutoff(recentForTradingDate);
  const visibleAtCutoff = atCutoff(visibleForTradingDate);

  if (recentAtCutoff.length > 0) return recentAtCutoff;
  if (visibleAtCutoff.length > 0) return visibleAtCutoff;

  return atCutoff(historicalForTradingDate);
}


// The MI header badge used to read `isAuthenticated ? 'LIVE' : connectionStatus`,
// which claimed LIVE all night next to a chart that said the market was closed.
// LIVE now means both things the word implies: the session is open AND the feed
// is authenticated. Everything else gets its own honest label.
//
// The websocket's own 'closed' status is reported as DISCONNECTED so that CLOSED
// only ever describes the exchange.
const MI_CONNECTION_LABELS = {
  closed: 'DISCONNECTED',
  auth_error: 'ERROR',
  idle: 'Loading...',
  connecting: 'Loading...',
  connected: 'Loading...',
};

// `setConnectionStatus` does not clear `isAuthenticated` -- only `setAuthError`
// does -- so after an ordinary drop or reconnect the flag stays true against a
// status of 'disconnected' / 'closed' / 'connected'. Trusting the flag alone
// would put LIVE on a dead socket during market hours, which is the same class
// of lie this derivation exists to stop. The socket's own status decides.
const MI_LIVE_STATUS = 'authenticated';

export function deriveMarketIntelligenceStatus({
  autoScroll,
  replaying,
  isAuthenticated,
  connectionStatus,
  error,
  marketOpen,
  hasRenderableData = false,
}) {
  if (!autoScroll) {
    return {
      label: 'PAUSED',
      className: 'misLive paused',
    };
  }

  if (replaying) {
    return {
      label: 'REPLAY',
      className: 'misLive replay',
    };
  }

  if (!marketOpen && hasRenderableData) {
    return {
      label: 'CLOSED',
      className: 'misLive',
    };
  }

  if (error) {
    return {
      label: 'ERROR',
      className: 'misLive',
    };
  }

  const raw =
    String(connectionStatus || 'idle');

  if (
    !isAuthenticated ||
    raw !== MI_LIVE_STATUS
  ) {
    const label =
      MI_CONNECTION_LABELS[raw] ||
      raw.toUpperCase();

    return {
      label,
      className: 'misLive',
    };
  }

  if (!marketOpen) {
    return {
      label: 'CLOSED',
      className: 'misLive',
    };
  }

  return {
    label: 'LIVE',
    className: 'misLive on',
  };
}


function MarketIntelligencePost({
  post,
  defaultOpen = false,
}) {
  const [open, setOpen] =
    useState(defaultOpen);

  const showLifecycleBadge = (
    post.lifecycleLabel &&
    post.lifecycleLabel !== post.statusLabel
  );

  const postClassName = [
    'misPost',
    post.moduleClass,
    post.priorityClass,
    open ? 'open' : '',
    post.lifecycleState
      ? `state-${post.lifecycleState}`
      : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <article
      className={postClassName}
      data-post-id={String(post.id)}
    >
      <button
        className="misPostToggle"
        type="button"
        onClick={() => (
          setOpen((prev) => !prev)
        )}
        aria-expanded={open}
      >
        <div className="misPostTop">
          <div className="misPostIdentity">

            <span className="misModuleBadge">
              {post.moduleLabel}
            </span>

            <span className="misStatusBadge">
              {post.statusLabel}
            </span>

            {showLifecycleBadge && (
              <span
                className={`misLifecycleBadge ${post.lifecycleState}`}
              >
                {post.lifecycleLabel}
              </span>
            )}

            <span className="misTime">
              {getTimeET(post.createdAt)} ET
            </span>

          </div>

          <span className="misChevron">
            {open ? '^' : 'v'}
          </span>
        </div>

        <h3>
          {post.headline}
        </h3>

        <div className="misQuickBullets">
          {post.summaryBullets
            .slice(0, 3)
            .map((bullet, index) => (
              <div
                key={`${post.id}-quick-${index}`}
              >
                <span>-</span>
                <p>{bullet}</p>
              </div>
            ))}
        </div>
      </button>

      {open && (
        <div className="misPostBodyWrap">

          <p className="misPostBody">
            {post.body}
          </p>

          {(post.bias || post.confidence) && (
            <div className="misReadMeta">

              {post.bias && (
                <div>
                  <span>Bias</span>
                  <strong>
                    {cleanLabel(post.bias)}
                  </strong>
                </div>
              )}

              {post.confidence && (
                <div>
                  <span>Confidence</span>
                  <strong>
                    {cleanLabel(post.confidence)}
                  </strong>
                </div>
              )}

            </div>
          )}

          {post.keyLevels.length > 0 && (
            <div className="misKeyLevels">
              {post.keyLevels.map((level) => (
                <div
                  key={`${post.id}-${level.label}`}
                >
                  <span>
                    {level.label}
                  </span>

                  <strong>
                    {level.value}
                  </strong>
                </div>
              ))}
            </div>
          )}

          {post.cardSections.length > 0 && (
            <div className="misCardSections">
              {post.cardSections.map(
                (section, index) => (
                  <section
                    key={`${post.id}-section-${index}`}
                  >
                    <h4>
                      {section.title}
                    </h4>

                    {section.items.map(
                      (item, itemIndex) => (
                        <p
                          key={`${post.id}-section-${index}-${itemIndex}`}
                        >
                          {typeof item === 'object'
                            ? JSON.stringify(item)
                            : String(item)}
                        </p>
                      )
                    )}
                  </section>
                )
              )}
            </div>
          )}

          {post.tags.length > 0 && (
            <div className="misTags">
              {post.tags.map((tag) => (
                <span
                  key={`${post.id}-${tag}`}
                >
                  {cleanLabel(tag)}
                </span>
              ))}
            </div>
          )}

          <div className="misPostFooter">
            <span>
              {post.agentLabel}
            </span>
          </div>

        </div>
      )}
    </article>
  );
}


export default function MarketIntelligenceFeedWidget() {
  const recentPosts =
    useMarketIntelligenceStore(
      (state) => state.recentFrontendPosts
    );

  const visiblePosts =
    useMarketIntelligenceStore(
      (state) => state.visibleFrontendPosts
    );

  const connectionStatus =
    useMarketIntelligenceStore(
      (state) => state.connectionStatus
    );

  const isAuthenticated =
    useMarketIntelligenceStore(
      (state) => state.isAuthenticated
    );

  const error =
    useMarketIntelligenceStore(
      (state) => state.error
    );

  const replayCutoffMs =
    usePlaybackStore(
      (state) => state.replayCutoffMs
    );

  const replaying =
    usePlaybackStore(
      (state) => state.mode === 'playing'
    );

  const replayTradingDate =
    usePlaybackStore(
      (state) => (
        state.replaySource?.tradingDate ||
        state.bufferDate ||
        null
      )
    );

  const replaySourcePosts =
    usePlaybackStore(
      (state) => (
        state.replaySource?.datasets?.mi ||
        state.replaySource?.miPosts ||
        EMPTY_POSTS
      )
    );

  const targetTradingDate = replayCutoffMs != null
    ? replayTradingDate || etDateKey(replayCutoffMs)
    : mostRecentSessionDate();

  const historicalPosts =
    useMarketIntelligenceStore(
      (state) => (
        replayCutoffMs == null
          ? state.historicalFrontendPosts
          : EMPTY_POSTS
      )
    );

  const displayTradingDate = useMemo(() => (replayCutoffMs == null
    ? [
      ...recentPosts,
      ...visiblePosts,
      ...historicalPosts,
    ].reduce((latestDate, post) => {
      const date = getPostTradingDate(post);
      if (!isMarketSessionDate(date) || date > targetTradingDate) return latestDate;
      return !latestDate || date > latestDate ? date : latestDate;
    }, null) || targetTradingDate
    : targetTradingDate), [
    historicalPosts,
    recentPosts,
    replayCutoffMs,
    targetTradingDate,
    visiblePosts,
  ]);

  const hasHydratedPosts =
    useMarketIntelligenceStore(
      (state) => state.hasHydratedPosts
    );

  const marketOpen =
    useMarketOpen();

  const feedRef =
    useRef(null);

  const previousNewestPostIdRef =
    useRef(null);

  const [autoScroll, setAutoScroll] =
    useState(true);

  const [alarmArmed, setAlarmArmed] =
    useState(() => (
      isMarketIntelligenceAlarmEnabled()
    ));

  const hasTargetDateHistoryAfterCutoff = useMemo(() => (
    replayCutoffMs != null &&
    [
      ...recentPosts,
      ...visiblePosts,
      ...replaySourcePosts,
    ].some((post) => (
      getPostTradingDate(post) === targetTradingDate &&
      getPostTimestampValue(getPostTimestamp(post)) > replayCutoffMs
    ))
  ), [
    recentPosts,
    replayCutoffMs,
    replaySourcePosts,
    targetTradingDate,
    visiblePosts,
  ]);

  const posts = useMemo(() => {
    return selectMarketIntelligencePostsForTradingDate({
      recentPosts,
      visiblePosts,
      historicalPosts,
      tradingDate: displayTradingDate,
      cutoffMs: replayCutoffMs,
    });
  }, [
    displayTradingDate,
    historicalPosts,
    recentPosts,
    replayCutoffMs,
    visiblePosts,
  ]);

  const historyStartsAfterCutoff =
    replayCutoffMs != null &&
    hasTargetDateHistoryAfterCutoff &&
    posts.length === 0;

  const targetDateUnavailable =
    hasHydratedPosts &&
    !historyStartsAfterCutoff &&
    posts.length === 0;

  const normalizedPosts = useMemo(() => {
    return buildMarketIntelligenceFeedPosts(
      posts
    );
  }, [posts]);

  const latestPost =
    normalizedPosts[0];

  const feedStatus =
    deriveMarketIntelligenceStatus({
      autoScroll,
      replaying,
      isAuthenticated,
      connectionStatus,
      error,
      marketOpen,
      hasRenderableData: normalizedPosts.length > 0,
    });

  const newestPostId =
    normalizedPosts[0]?.id ||
    null;

  useLayoutEffect(() => {
    if (!feedRef.current) return;

    const feed =
      feedRef.current;

    const previousNewestPostId =
      previousNewestPostIdRef.current;

    const hasNewTopPost = (
      Boolean(newestPostId) &&
      Boolean(previousNewestPostId) &&
      newestPostId !== previousNewestPostId
    );

    if (hasNewTopPost) {
      if (autoScroll) {
        feed.scrollTop = 0;
      } else {
        const postElements = Array.from(
          feed.querySelectorAll(
            '[data-post-id]'
          )
        );

        const previousNewestPost =
          postElements.find(
            (post) => (
              post.dataset.postId ===
              String(previousNewestPostId)
            )
          );

        const newestPost =
          postElements[0];

        const prependedHeight =
          previousNewestPost &&
          newestPost
            ? previousNewestPost.offsetTop -
              newestPost.offsetTop
            : 0;

        if (prependedHeight > 0) {
          feed.scrollTo({
            top:
              feed.scrollTop +
              prependedHeight,
            behavior: 'instant',
          });
        }
      }
    }

    previousNewestPostIdRef.current =
      newestPostId;
  });

  const handleScroll = () => {
    if (!feedRef.current) return;

    setAutoScroll(
      feedRef.current.scrollTop < 80
    );
  };

  const handleAlarmToggle = async () => {
    if (alarmArmed) {
      disableMarketIntelligenceAlarm();
      setAlarmArmed(false);
      return;
    }

    setAlarmArmed(
      await enableMarketIntelligenceAlarm()
    );
  };

  return (
    <section className="misTerminal">

      <WidgetPanelStatus>
        <span className="misTerminalDate">
          {latestPost?.tradingDate || targetTradingDate || '-'}
        </span>
        {feedStatus.label !== 'REPLAY' && (
          <span className={feedStatus.className}>
            {feedStatus.label}
          </span>
        )}
      </WidgetPanelStatus>

      <WidgetPanelControls
        className="misTerminalControls"
      >
        <button
          className={
            alarmArmed
              ? 'misAlarmToggle on'
              : 'misAlarmToggle'
          }
          type="button"
          onClick={handleAlarmToggle}
          aria-pressed={alarmArmed}
        >
          {alarmArmed
            ? 'ALERT ON'
            : 'ENABLE ALERT'}
        </button>

      </WidgetPanelControls>

      <div
        className="misTerminalFeed"
        ref={feedRef}
        onScroll={handleScroll}
      >
        {normalizedPosts.length === 0 ? (
          <div className="misNoPosts">

            <h3>
              {historyStartsAfterCutoff
                ? 'MI history starts after this replay time'
                : targetDateUnavailable
                  ? `MI unavailable for ${targetTradingDate}`
                : 'Loading...'}
            </h3>

            {!historyStartsAfterCutoff && error && (
              <p>
                {error}
              </p>
            )}

          </div>
        ) : (
          normalizedPosts.map(
            (post, index) => (
              <MarketIntelligencePost
                key={post.id}
                post={post}
                defaultOpen={index === 0}
              />
            )
          )
        )}
      </div>

    </section>
  );
}
